<template>
  <div
    class="tad-dropzone"
    :class="{
      'tad-dropzone--hover': isHover && !disabled,
      'tad-dropzone--dragging': isDragging && !disabled,
      'tad-dropzone--has-files': showAttachedFiles && displayItems.length > 0,
      'tad-dropzone--disabled': disabled,
    }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
    @mouseenter="isHover = true"
    @mouseleave="isHover = false"
  >
    <input
      v-if="!disabled"
      :id="inputId"
      ref="fileInput"
      type="file"
      class="tad-dropzone__input"
      :multiple="multiple"
      :accept="accept"
      tabindex="-1"
      @change="onFileChange"
    />

    <div
      v-if="showAttachedFiles && displayItems.length"
      class="tad-dropzone__files"
    >
      <div
        v-for="file in displayItems"
        :key="file.key"
        class="approval-file-chip tad-dropzone__file-chip"
      >
        <v-icon :icon="file.icon" size="14" />
        <span class="approval-file-chip__name" :title="file.name">{{
          file.name
        }}</span>
        <button
          v-if="file.removable && !disabled"
          type="button"
          class="approval-file-chip__action"
          :aria-label="`Remove ${file.name}`"
          @click.stop.prevent="removeFile(file)"
        >
          <v-icon icon="mdi-close" size="14" />
        </button>
      </div>
    </div>

    <label
      v-if="!disabled"
      class="tad-dropzone__inner"
      :for="inputId"
      @click="onBrowseClick"
    >
      <div class="tad-dropzone__icon-ring">
        <v-icon icon="mdi-cloud-upload-outline" size="32" />
      </div>
      <p class="tad-dropzone__title">{{ title }}</p>
      <p class="tad-dropzone__hint">{{ hint }}</p>
    </label>
  </div>
</template>

<script>
let dropzoneUid = 0;

export default {
  name: "TicketActionDropzone",
  props: {
    modelValue: { default: null },
    multiple: { type: Boolean, default: false },
    accept: { type: String, default: "" },
    title: { type: String, default: "Drop files here or click to browse" },
    hint: { type: String, default: "PDF, images, and documents supported" },
    showAttachedFiles: { type: Boolean, default: true },
    existingFiles: { type: Array, default: () => [] },
    existingFileNameKey: { type: String, default: "file_name" },
    existingFileTypeKey: { type: String, default: "file_type" },
    disabled: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "remove-existing"],
  data() {
    return {
      isHover: false,
      isDragging: false,
      localFiles: [],
      isModelBound: false,
      pickerLocked: false,
      inputId: `tad-dropzone-input-${++dropzoneUid}`,
      lockTimer: null,
    };
  },
  computed: {
    pendingFiles() {
      if (this.isModelBound) {
        if (!this.modelValue) return [];
        return Array.isArray(this.modelValue)
          ? this.modelValue
          : [this.modelValue];
      }
      return this.localFiles;
    },
    displayItems() {
      if (!this.showAttachedFiles) return [];

      const existing = (this.existingFiles || []).map((file, index) => ({
        key: `existing-${index}-${file[this.existingFileNameKey] || index}`,
        name: file[this.existingFileNameKey] || `Attachment ${index + 1}`,
        icon: this.resolveFileIcon(
          file[this.existingFileTypeKey],
          file[this.existingFileNameKey],
        ),
        removable: true,
        source: "existing",
        index,
      }));

      const pending = this.pendingFiles.map((file, index) => ({
        key: `pending-${index}-${file?.name || "file"}-${file?.size ?? index}`,
        name: file?.name || `File ${index + 1}`,
        icon: this.resolveFileIcon(file?.type, file?.name),
        removable: true,
        source: "pending",
        index,
      }));

      return [...existing, ...pending];
    },
  },
  watch: {
    // Shallow only — deep-watching File objects can hang the page.
    modelValue(val) {
      if (!this.isModelBound) return;
      this.localFiles = !val ? [] : Array.isArray(val) ? [...val] : [val];
    },
  },
  created() {
    const vnodeProps = this.$.vnode?.props || {};
    this.isModelBound =
      Object.prototype.hasOwnProperty.call(vnodeProps, "modelValue") ||
      Object.prototype.hasOwnProperty.call(vnodeProps, "model-value") ||
      Object.prototype.hasOwnProperty.call(vnodeProps, "onUpdate:modelValue");
    if (this.isModelBound && this.modelValue) {
      this.localFiles = Array.isArray(this.modelValue)
        ? [...this.modelValue]
        : [this.modelValue];
    }
  },
  beforeUnmount() {
    if (this.lockTimer) clearTimeout(this.lockTimer);
  },
  methods: {
    /**
     * Label[for] already opens the file dialog. Prevent a second programmatic
     * open, and briefly lock after any open so the browser "ghost click" that
     * fires when the OS dialog closes cannot reopen it in a loop (page hang).
     */
    onBrowseClick(event) {
      if (this.disabled) return;
      if (this.pickerLocked) {
        event.preventDefault();
        return;
      }
      this.lockPicker(1000);
      // Let the native label→input activation happen once (no input.click()).
    },
    lockPicker(ms) {
      this.pickerLocked = true;
      if (this.lockTimer) clearTimeout(this.lockTimer);
      this.lockTimer = setTimeout(() => {
        this.pickerLocked = false;
        this.lockTimer = null;
      }, ms);
    },
    onDragEnter() {
      if (!this.disabled) this.isDragging = true;
    },
    onDragOver() {
      if (!this.disabled) this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    setPendingFiles(next) {
      const list = !next ? [] : Array.isArray(next) ? next : [next];
      this.localFiles = list;
      this.$emit("update:modelValue", this.multiple ? list : list[0] || null);
    },
    emitFiles(fileList) {
      if (!fileList || fileList.length === 0 || this.disabled) return;
      const files = Array.from(fileList);
      if (this.multiple) {
        this.setPendingFiles([...this.pendingFiles, ...files]);
        return;
      }
      this.setPendingFiles(files[0] ? [files[0]] : []);
    },
    onFileChange(event) {
      const files = event.target.files;
      this.emitFiles(files);
      // Reset so selecting the same file again still fires change.
      event.target.value = "";
      this.isDragging = false;
      // Absorb post-dialog ghost click that would re-open the picker.
      this.lockPicker(800);
    },
    onDrop(event) {
      if (this.disabled) return;
      this.isDragging = false;
      this.emitFiles(event.dataTransfer?.files);
    },
    resolveFileIcon(mimeOrType, fileName) {
      const type = String(mimeOrType || "").toLowerCase();
      const name = String(fileName || "").toLowerCase();

      if (type.includes("pdf") || name.endsWith(".pdf")) {
        return "mdi-file-pdf-box";
      }
      if (
        type.includes("image") ||
        /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name)
      ) {
        return "mdi-file-image";
      }
      if (
        type.includes("sheet") ||
        type.includes("excel") ||
        /\.(xls|xlsx|csv)$/.test(name)
      ) {
        return "mdi-file-excel";
      }
      if (type.includes("word") || /\.(doc|docx)$/.test(name)) {
        return "mdi-file-word";
      }
      return "mdi-file-outline";
    },
    removeFile(file) {
      if (file.source === "existing") {
        this.$emit("remove-existing", file.index);
        return;
      }

      const next = this.pendingFiles.filter((_, index) => index !== file.index);
      this.setPendingFiles(next);
    },
  },
};
</script>
